import { z } from 'zod';
import { userRoleSchema } from './enums.js';
/**
 * Contrats des LIENS D'ACCÈS communautaires (Incrément A1) — source unique
 * front + back.
 *
 * Un lien d'accès = jeton opaque, révocable, lié à un compte PRÉ-EXISTANT précis
 * (lecteur read-only OU contributeur write). Présenter `?access=<jeton>` ouvre une
 * VRAIE session pour ce compte, sans mot de passe. Le lien N'ACCORDE rien par
 * lui-même : le niveau d'accès (lecture/écriture) découle du contrôle d'accès du
 * compte cible (grantee → read ; propriétaire → write).
 *
 * Le jeton BRUT (32 octets base64url) n'est jamais persisté : la base ne stocke que
 * son SHA-256. Il n'est renvoyé au client QU'UNE SEULE FOIS, à la création
 * (`sessionLinkCreatedSchema.secret`) — impossible à ré-afficher ensuite. Les liens
 * sont gérés par le SUPERADMIN uniquement (pas self-service).
 *
 * - `sessionLinkSchema`                : item de liste (jamais le jeton brut).
 * - `sessionLinkListSchema`            : `{ links }`.
 * - `createSessionLinkSchema`          : corps de création (`{ userId, label }`).
 * - `sessionLinkCreatedSchema`         : réponse de création (item + jeton brut, une fois).
 * - `sessionLinkExchangeRequestSchema` : corps de l'échange public (`{ token }`).
 */
/** Longueur maximale d'un libellé de lien (garde-fou contrat). */
export const SESSION_LINK_LABEL_MAX = 120;
/**
 * Item de lien d'accès (`GET /api/session-links`) — jamais le jeton brut ni son
 * hash. `revokedAt` non nul = lien révoqué (conservé pour l'historique, inutilisable).
 * Porte l'identité du compte cible (`targetUserId`/`targetEmail`/`targetRole`) pour
 * l'affichage dans la console superadmin.
 */
export const sessionLinkSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    /** Dernière utilisation (mise à jour à chaque échange), ou `null`. */
    lastUsedAt: z.string().datetime({ offset: true }).nullable(),
    /** Date de révocation (soft), ou `null` si le lien est actif. */
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    /** Compte cible ouvert par ce lien. */
    targetUserId: z.uuid(),
    targetEmail: z.string(),
    targetRole: userRoleSchema,
});
/** Liste des liens d'accès (console superadmin). */
export const sessionLinkListSchema = z.object({
    links: z.array(sessionLinkSchema),
});
/**
 * Corps de `POST /api/session-links` : le compte cible + un libellé lisible.
 * Sans `.transform` (piège boot-crash Zod v4 : `z.toJSONSchema` au boot) — la
 * normalisation éventuelle se fait dans le handler.
 */
export const createSessionLinkSchema = z.object({
    userId: z.uuid(),
    label: z.string().trim().min(1).max(SESSION_LINK_LABEL_MAX),
});
/**
 * Réponse de `POST /api/session-links` : l'item créé + le JETON BRUT.
 *
 * ⚠ `secret` n'est renvoyé QUE par cette réponse, UNE SEULE FOIS : il n'est jamais
 * stocké en clair ni ré-affichable ensuite. Le client doit inviter le superadmin à
 * copier immédiatement l'URL `?access=<secret>`.
 */
export const sessionLinkCreatedSchema = z.object({
    link: sessionLinkSchema,
    /** Jeton brut (base64url), affiché une seule fois côté client. */
    secret: z.string(),
});
/**
 * Corps de `POST /api/auth/link` (échange PUBLIC) : le jeton présenté dans l'URL
 * `?access=<token>`. Un jeton valide est échangé contre une session. Sans
 * `.transform` (piège boot-crash Zod v4).
 */
export const sessionLinkExchangeRequestSchema = z.object({
    token: z.string().min(1).max(512),
});
//# sourceMappingURL=session-link.js.map